import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session from the httpOnly cookie on mount
  useEffect(function () {
    let active = true;
    async function checkSession() {
      try {
        const res = await fetch(API_URL + '/api/auth/me', { credentials: 'include' });
        if (res.ok && active) {
          setUser(await res.json());
        }
      } catch (err) {
        // not authenticated / network issue
      } finally {
        if (active) setLoading(false);
      }
    }
    checkSession();
    return function () { active = false; };
  }, []);

  async function login(email, password) {
    setError(null);
    try {
      const response = await fetch(API_URL + '/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email, password: password })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Login failed');
      }
      setUser(data.user);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }

  async function register(email, password, accessCode) {
    setError(null);
    try {
      const response = await fetch(API_URL + '/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email, password: password, access_code: accessCode })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Registration failed');
      }
      setUser(data.user);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }

  async function logout() {
    try {
      await fetch(API_URL + '/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (err) {
      // ignore
    }
    setUser(null);
  }

  var value = {
    user: user,
    loading: loading,
    error: error,
    isAuthenticated: !!user,
    login: login,
    register: register,
    logout: logout,
    setError: setError
  };

  return React.createElement(AuthContext.Provider, { value: value }, children);
}
